import { Box } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import {
    ForwardRefRenderFunction,
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useState
} from 'react'
import { getClosureReports } from 'services/report'
import formatter from 'utils/formatter'
import { defaultHeaders } from '../../constants'
import {
    ClosureElements,
    ClosureFilterDTO,
    ClosureOperation
} from '../../types'
import { loaders } from './constants'

interface IPropsModelTable {
    filter: ClosureFilterDTO
}

interface IModeTableRef {
    resetPage: () => void
}

const ModeTableFC: ForwardRefRenderFunction<IModeTableRef, IPropsModelTable> = (
    { filter },
    refModeTable
) => {
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(1)
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const { currentInstitution, currentAgent } = useInstitutionsContext()

    useImperativeHandle(refModeTable, () => ({
        resetPage() {
            setInitialPage()
        }
    }))

    const [table, setTable] = useState<TableBuildProps<ClosureElements>>({
        ...defaultHeaders
    })

    const setInitialPage = () => setPage(1)

    const buildTable = (listFinanceReports: ClosureOperation[]) => {
        return listFinanceReports.map((report: ClosureOperation) => {
            return {
                ...report,
                totalTaxaRegistro: formatter.moeda(report.totalTaxaRegistro),
                totalValorRegistradora: formatter.moeda(
                    report.totalValorRegistradora
                )
            }
        })
    }

    async function fetchListClosureReports() {
        setLoading(true)
        try {
            const response = await getClosureReports(
                {
                    pagina: page,
                    limite: limit,
                    ...filter,
                    cnpjAgente: currentAgent?.cnpj ?? ''
                },
                currentInstitution?.id ?? ''
            )

            setTotalPages(+response.headers['total-pages'])
            setTable((old: any) => ({
                ...old,
                header: table.header,
                body: buildTable(response.data.operacoes)
            }))
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)

        if (currentInstitution?.id !== undefined) {
            fetchListClosureReports()
        }
    }, [page, limit, currentInstitution?.id, filter, currentAgent?.cnpj])

    const handlePage = (value: number) => {
        if (value !== page) {
            setPage(value)
            setLoading(true)
        }
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setPage(1)
        setLoading(true)
    }

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                loading={{
                    status: loading,
                    loader: loaders
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            ></TableBuild>
        </Box>
    )
}

export const ModeTable = memo(forwardRef(ModeTableFC))
